import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Clock, MessageSquare, Send, Instagram, Linkedin, HelpCircle } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AnimatedSection } from "@/components/ui/animated-section";
import { staggerContainer, staggerItem } from "@/lib/animations";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

// Contact info items
const contactInfo = [
  {
    icon: Mail,
    title: "Email Us",
    description: "Our team typically responds within 24 hours",
    value: "support@survi.ai",
    href: "mailto:support@survi.ai",
  },
  {
    icon: Clock,
    title: "Support Hours",
    description: "When our team is available",
    value: "Mon-Fri, 9AM-6PM EST",
    href: null,
  },
  {
    icon: MessageSquare,
    title: "Live Chat",
    description: "Chat with our support team",
    value: "Available in-app",
    href: null,
  },
];

// Social links (matches footer)
const socialLinks = [
  { icon: "X", label: "X (formerly Twitter)", href: "https://x.com/SurviAi" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/surviai/" },
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/surviai" },
];

// Subject options
const subjectOptions = [
  { value: "general", label: "General Inquiry" },
  { value: "support", label: "Technical Support" },
  { value: "sales", label: "Sales & Pricing" },
  { value: "partnership", label: "Partnership Opportunity" },
  { value: "feedback", label: "Product Feedback" },
  { value: "other", label: "Other" },
];

export default function Contact() {
  useDocumentMeta({
    title: 'Contact Us',
    description: "Get in touch with the SurviAI team. We're here to help with questions, feedback, or support."
  });

  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: "Message sent!",
      description: "We'll get back to you as soon as possible.",
    });
    
    setFormData({ name: "", email: "", subject: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero Section */}
        <Section className="pt-24 md:pt-32 pb-16">
          <Container>
            <AnimatedSection className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Get in <span className="text-gradient">Touch</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                Have a question, feedback, or need help? We're here to assist you. 
                Fill out the form below and our team will get back to you shortly.
              </p>
            </AnimatedSection>
          </Container>
        </Section>

        {/* Contact Form + Info Section */}
        <Section background="muted">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-3">
                <AnimatedSection>
                  <Card>
                    <CardHeader>
                      <CardTitle>Send us a message</CardTitle>
                      <CardDescription>
                        Fill out the form and we'll respond within 24 hours
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Name</Label>
                            <Input
                              id="name"
                              placeholder="Your name"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="you@example.com"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="subject">Subject</Label>
                          <Select
                            value={formData.subject}
                            onValueChange={(value) => setFormData({ ...formData, subject: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a subject" />
                            </SelectTrigger>
                            <SelectContent>
                              {subjectOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="message">Message</Label>
                          <Textarea
                            id="message"
                            placeholder="Tell us how we can help..."
                            rows={6}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            required
                          />
                        </div>
                        
                        <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <span className="animate-spin mr-2">○</span>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              </div>
              
              {/* Contact Info Sidebar */}
              <div className="lg:col-span-2">
                <motion.div
                  className="space-y-6"
                  variants={staggerContainer}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                >
                  {/* Contact Info Cards */}
                  {contactInfo.map((item) => (
                    <motion.div key={item.title} variants={staggerItem}>
                      <Card className="hover-lift">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <item.icon className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">{item.title}</h3>
                              <p className="text-sm text-muted-foreground mb-1">{item.description}</p>
                              {item.href ? (
                                <a 
                                  href={item.href}
                                  className="text-sm font-medium text-primary hover:underline"
                                >
                                  {item.value}
                                </a>
                              ) : (
                                <p className="text-sm font-medium text-foreground">{item.value}</p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                  
                  {/* Social Links */}
                  <motion.div variants={staggerItem}>
                    <Card>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold text-foreground mb-4">Follow Us</h3>
                        <div className="flex gap-3">
                          {socialLinks.map((social) => (
                            <a
                              key={social.label}
                              href={social.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                              aria-label={social.label}
                            >
                              {social.icon === "X" ? (
                                <svg
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                              ) : (
                                <social.icon className="h-5 w-5" />
                              )}
                            </a>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  
                  {/* FAQ Link */}
                  <motion.div variants={staggerItem}>
                    <Card className="bg-primary/5 border-primary/20">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">Need Quick Answers?</h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              Check our FAQ for instant help
                            </p>
                            <Link to="/#faq">
                              <Button variant="link" className="p-0 h-auto text-primary">
                                View FAQ →
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </Container>
        </Section>

        {/* Optional: Location/Map Section */}
        <Section>
          <Container>
            <AnimatedSection className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Our Headquarters
              </h2>
              <p className="text-muted-foreground mb-6">
                While we're a remote-first company, our headquarters is located in San Francisco, CA.
              </p>
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center border">
                <div className="text-center text-muted-foreground">
                  <p className="text-lg font-medium">San Francisco, CA</p>
                  <p className="text-sm">United States</p>
                </div>
              </div>
            </AnimatedSection>
          </Container>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
